/**
 * 事件上报 API
 * @module core/api/event
 */

import type { HttpClient } from '../http/HttpClient'
import { API_ENDPOINTS } from '../../constants/api-endpoints'
import { stringifyQueryParams } from '../utils/url'

const EVENT_REPORT_TIMEOUT_MS = 2500

// ============================================
// 类型定义
// ============================================

/** 事件上报参数 */
export interface EventReportParams {
  /** 软件 ID */
  soft_id: string
  /** 来源 */
  source: string
  /** 设备码 */
  device_code: string
  /** 事件 ID */
  event_id: string
  /** 推广策略 */
  strategy?: string
  /** 设备密钥 */
  ekey?: string
}

/** 百度事件参数 */
export interface BaiduEventParams {
  /** 百度 VID */
  bdVid: string
  /** 推广策略 */
  strategy: string
  /** 事件类型 */
  eventType: number
}

// ============================================
// API 类
// ============================================

export class EventAPI {
  constructor(private httpClient: HttpClient) {}

  /** 百度回传事件 */
  async reportBaiduEvent(params: BaiduEventParams): Promise<void> {
    await this.httpClient.post(API_ENDPOINTS.EVENT.BAIDU_REPORT, params, {
      timeout: EVENT_REPORT_TIMEOUT_MS
    })
  }

  /**
   * 通用事件上报
   * @param eventId - 事件 ID
   * @param strategy - 推广策略
   */
  async reportEvent(eventId: string, strategy?: string): Promise<void> {
    const params: Record<string, string> = {
      event_id: eventId
    }

    if (strategy) {
      params.strategy = strategy
    }

    const queryString = stringifyQueryParams(params)
    await this.httpClient.get(`${API_ENDPOINTS.EVENT.REPORT}?${queryString}`, {
      timeout: EVENT_REPORT_TIMEOUT_MS
    })
  }

  /** 安装事件 */
  async reportInstallEvent(strategy?: string): Promise<void> {
    return this.reportEvent('install', strategy)
  }

  /** 上线事件 */
  async reportOnlineEvent(strategy?: string): Promise<void> {
    return this.reportEvent('online', strategy)
  }

  /** 访问事件 */
  async reportVisitEvent(strategy?: string): Promise<void> {
    return this.reportEvent('visit', strategy)
  }

  /**
   * 自定义事件
   * @param eventNum - 事件编号
   * @param strategy - 推广策略
   */
  async reportCustomEvent(eventNum: number, strategy?: string): Promise<void> {
    return this.reportEvent(`customevent_${eventNum}`, strategy)
  }
}
